import React, { Fragment } from 'react';
import { useNavigate } from "react-router-dom";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';

const columns = [
    { id: 'img', label: '' },
    { id: 'category', label: 'Category' },
    { id: 'subcategory', label: 'Sub Category' },
    { id: 'vendor', label: 'Vendor' },
    { id: 'manufacturer', label: 'Manufacturer' },
    { id: 'name', label: 'Name' },
    { id: 'status', label: 'Status' },
    { id: 'location', label: 'Location' },
    { id: 'color', label: 'Color' },
    { id: 'size', label: 'Size' },
    { id: 'quantity', label: 'Quantity' },
    { id: 'value', label: 'Value' },
];

export default function DataTable(props) {
    const { b2Auth, businessRid, loading, data, currentLimit, setCurrentLimit, getPage, setGetPage, sort, setSort, sortDir, setSortDir } = props;

    let navigate = useNavigate();
    const theme = useTheme();

    let data_totalResults = 0, data_totalPages = 0, data_getPage = 0, data_currentLimit = 0, paginationData, data_sortField, data_sortDir, dataToRender;

    if (data) {
        data_totalResults = parseInt(data?.swaginventorys[0].totalResults);
        data_totalPages = parseInt(data?.swaginventorys[0].totalPages);
        data_getPage = parseInt(data?.swaginventorys[0].getPage);
        data_currentLimit = parseInt(data?.swaginventorys[0].currentLimit);
        paginationData = { data_totalResults, data_totalPages, data_getPage, data_currentLimit };
        data_sortField = data?.swaginventorys[0].sortField;
        data_sortDir = data?.swaginventorys[0].sortDir;
        dataToRender = data?.swaginventorys[0].results;
    }

    const handleChangePage = (event, newPage) => {
        setGetPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setCurrentLimit(+event.target.value);
        setGetPage(0);
    };

    const createSortHandler = (property) => (event) => {
        handleRequestSort(event, property);
    };

    const handleRequestSort = (event, property) => {
        const isAsc = sort === property && sortDir === 'asc';
        setSort(property);
        setSortDir(isAsc ? 'desc' : 'asc');
    };

    const clickRow = (rid) => {
        navigate(`/inventory/adjust/${rid}`);
    }

    return (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>

            <Grid container>
                <Grid item>
                    {/* Filter
                    Category
                    Vendor
                    Status
                    Quantity > 0 */}
                </Grid>
            </Grid>

            {loading ?
                <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
                    <CircularProgress sx={{ color: '#c9fb00' }} thickness={1} size={60} />
                </Backdrop>
                :
                <Fragment>
                    <TableContainer>
                        <Table stickyHeader aria-label="sticky table">
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align={column.align}
                                            style={{ minWidth: column.minWidth }}
                                        >
                                            <TableSortLabel
                                                active={sort === column.id}
                                                direction={sort === column.id ? sortDir : 'asc'}
                                                onClick={createSortHandler(column.id)}
                                            >
                                                {column.label}
                                                {sort === column.id ? (
                                                    <Box component="span" sx={visuallyHidden}>
                                                        {sortDir === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                                    </Box>
                                                ) : null}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {data_totalResults > 0 ?
                                dataToRender
                                    .map((row) => {
                                        let imgSrc = "";
                                        let displayImages = "";
                                        const currentImages = row.images;

                                        if(currentImages && currentImages !== "[]"){
                                            const displayImagesPreSort = JSON.parse(currentImages);
                                            try{
                                                displayImages = displayImagesPreSort.sort((a, b)=>{return a.displayOrder - b.displayOrder;});
                                                if(displayImages.length > 0){
                                                    const imgSrcVal = displayImages[0]["key"];
                                                    if(imgSrcVal){
                                                        imgSrc = `https://f004.backblazeb2.com/file/swagmanager/docs/swag/clients/${businessRid}/images/swag_${row.rid}_s_${imgSrcVal}?Authorization=${b2Auth}`;
                                                    }
                                                }
                                            }
                                            catch{displayImages = [];}
                                        }

                                        let dispValue = "", dispCurrency = "";
                                        const currency = row.currency;
                                        const value = row.value;
                                        if (currency){
                                            const splitCurrency = currency.split("-");
                                            if(splitCurrency[1]){dispCurrency = splitCurrency[1];}
                                            else{dispCurrency = currency;}
                                        }
                                        if(currency || value){dispValue = `${dispCurrency} ${value}`;}

                                        return (
                                            <TableRow hover role="button" tabIndex={-1} key={row.rid} onClick={()=>clickRow(row.rid)} sx={{cursor: 'pointer'}}>
                                                <TableCell>
                                                    <Avatar alt={`${row?.swag?.manufacturer} - ${row?.swag?.name}`} src={imgSrc} />
                                                </TableCell>                                               
                                                <TableCell>{row.swag?.category}</TableCell>
                                                <TableCell>{row.swag?.subcategory}</TableCell>
                                                <TableCell>{row.swag?.vendor}</TableCell>
                                                <TableCell>{row.swag?.manufacturer}</TableCell>
                                                <TableCell>{row.swag?.name}</TableCell>
                                                <TableCell>{row.status}</TableCell>
                                                <TableCell>{row.location}</TableCell>
                                                <TableCell>{row.color}</TableCell>
                                                <TableCell>{row.size}</TableCell>
                                                <TableCell>{row.quantity}</TableCell>
                                                <TableCell>{dispValue}</TableCell>
                                            </TableRow>
                                        );
                                    })
                                :
                                <TableRow>
                                    <TableCell colSpan="100%">No Results</TableCell>
                                </TableRow>
                                }

                            </TableBody>

                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 100]}
                        component="div"
                        count={data_totalResults}
                        rowsPerPage={currentLimit}
                        page={getPage}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Fragment>
            }
        </Paper>
    );
}